import React, { useContext, useEffect, useState } from "react";
import {
    LottieLoadingIndicator,
    PortalHighlightMetricCard,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    SettingsActionButton,
    SettingsBodyText,
    SettingsInfoBox,
    SettingsPanelCaption,
    SettingsPanelHeader,
    SettingsPanelStack,
    SettingsPanelTitle,
    SettingsSectionBody,
    SettingsSectionCard,
    SettingsSectionHeader,
    SettingsSectionIcon,
    SettingsSectionTitle,
    Toast
} from "src/components";
import HeaderIcon from "src/img/Storage.png";
import ManageStorageModal from "../components/ManageStorageModal";
import StorageUpgradeModal from "../components/StorageUpgradeModal";
import { SettingContext } from "../SettingContext";
import {
    formatStorageGb,
    formatStoragePercentage,
    formatStorageSize,
    getStorageSummary,
    normaliseStorageSummary,
    STORAGE_CATEGORIES
} from "../api/storageApi";
const emptySummary = normaliseStorageSummary({
    limitGb: 10,
    usedGb: 0,
    categories: STORAGE_CATEGORIES.map(category => ({   
        ...category,
        count: 0,
        percentage: 0
    }))
});

function UsageOverview({ summary, onPurchase }) {
    const usedLabel = formatStorageSize(summary.usedBytes);
    const freeLabel = formatStorageSize(summary.freeBytes);
    const percentageLabel = formatStoragePercentage(summary.usagePercentage);

    return (
        <PortalHighlightMetricCard minHeight="12rem" gridRow="span 2">
            <PortalMetricLabel>Storage allowance</PortalMetricLabel>
            <PortalMetricValue>{formatStorageGb(summary.limitGb)} GB</PortalMetricValue>
            <SettingsBodyText>
                {usedLabel} used ({percentageLabel}%). {freeLabel} remaining.
            </SettingsBodyText>
            <SettingsActionButton type="button" minWidth="13rem" onClick={onPurchase}>
                Purchase More Storage
            </SettingsActionButton>
        </PortalHighlightMetricCard>
    );
}

function CategoryOverview({ categories }) {
    return (
        <>
            {categories.map(category => (
                <PortalMetricCard key={category.key || category.label} minHeight="7.5rem">
                    <PortalMetricLabel>{category.label}</PortalMetricLabel>
                    <PortalMetricValue size="1.5rem">
                        {category.count.toLocaleString()}
                    </PortalMetricValue>
                    <SettingsBodyText size="0.8125rem">
                        {formatStoragePercentage(category.percentage)}% of used storage
                    </SettingsBodyText>
                </PortalMetricCard>
            ))}
        </>
    );
}

export default function StorageManagement() {
    const { isAccountOwner } = useContext(SettingContext);
    const [summary, setSummary] = useState(emptySummary);
    const [loading, setLoading] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [showManageModal, setShowManageModal] = useState(false);

    const loadSummary = async () => {
        setLoading(true);
        try {
            const response = await getStorageSummary();
            setSummary(response);
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSummary();
    }, []);

    return (
        <>
            <SettingsPanelStack marginTop="2rem">
                <SettingsPanelHeader withDivider>
                    <div>
                        <SettingsPanelTitle>Storage Management</SettingsPanelTitle>
                        <SettingsPanelCaption>
                            View your current storage usage and remaining space. Upgrade your storage allowance when your business needs more room.
                        </SettingsPanelCaption>
                    </div>
                </SettingsPanelHeader>

                <SettingsSectionCard>
                    <SettingsSectionHeader background="#171A22" borderColor="#171A22">
                        <SettingsSectionIcon src={HeaderIcon} alt="" />
                        <SettingsSectionTitle color="#FFFFFF">Storage Overview</SettingsSectionTitle>
                    </SettingsSectionHeader>

                    <SettingsSectionBody>
                        {loading ? (
                            <SettingsInfoBox justifyContent="center" padding="2rem">
                                <LottieLoadingIndicator />
                            </SettingsInfoBox>
                        ) : (
                            <>
                                <PortalMetricGrid columns="3">
                                    <UsageOverview
                                        summary={summary}
                                        onPurchase={() => setShowUpgradeModal(true)}
                                    />
                                    <CategoryOverview categories={summary.categories} />
                                </PortalMetricGrid>

                                {isAccountOwner && (
                                    <SettingsInfoBox>
                                        <div>
                                            <SettingsSectionTitle>Manage stored files</SettingsSectionTitle>
                                            <SettingsBodyText>
                                                View or delete files stored in your business account.
                                            </SettingsBodyText>
                                        </div>
                                        <SettingsActionButton
                                            type="button"
                                            minWidth="10rem"
                                            onClick={() => setShowManageModal(true)}
                                        >
                                            Manage Storage
                                        </SettingsActionButton>
                                    </SettingsInfoBox>
                                )}
                            </>
                        )}
                    </SettingsSectionBody>
                </SettingsSectionCard>
            </SettingsPanelStack>

            {showUpgradeModal && (
                <StorageUpgradeModal
                    show={showUpgradeModal}
                    onClose={() => setShowUpgradeModal(false)}
                    summary={summary}
                    onPurchased={loadSummary}
                />
            )}

            {showManageModal && (
                <ManageStorageModal
                    show={showManageModal}
                    onClose={() => setShowManageModal(false)}
                    onPurchase={() => setShowUpgradeModal(true)}
                    summary={summary}
                    onChanged={loadSummary}
                />
            )}
        </>
    );
}
